import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Building2, GraduationCap, ListChecks, Landmark, ArrowRight, UserSquare, Users, FileText, MapPin, BookOpen, Images } from "lucide-react";
import { Link } from "react-router-dom";

const StatCard = ({ label, value, hint, icon: Icon, accent = "#234333", testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#e5e0d8] rounded-sm p-6 hover:border-[#234333]/40 transition-colors"
  >
    <div className="flex items-start justify-between">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
        {label}
      </div>
      <Icon size={18} strokeWidth={1.5} style={{ color: accent }} />
    </div>
    <div className="mt-4 font-serif text-4xl text-[#1c1a17]">{value}</div>
    {hint && <div className="mt-2 text-xs text-[#6b655b]">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setSummary(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
          Administrative overview
        </div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-[#1c1a17] leading-tight">
          {summary?.trust_name ? summary.trust_name : "GS Mandal Trust"}
        </h1>
        <div className="mt-3 flex items-center gap-3 text-sm text-[#6b655b]">
          {summary?.short_name && (
            <span className="px-2 py-0.5 border border-[#e5e0d8] rounded-sm text-xs tracking-wide uppercase">
              {summary.short_name}
            </span>
          )}
          <span className="inline-flex items-center gap-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                summary?.trust_status === "Dormant" ? "bg-[#9e2a2b]" : "bg-[#3a5a40]"
              }`}
            />
            {summary?.trust_status || "Status not set"}
          </span>
        </div>
        <div className="serif-rule mt-6 max-w-sm" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          testid="stat-institutes"
          label="Institutes"
          value={loading ? "—" : summary?.institute_count ?? 0}
          hint="Under GS Mandal umbrella"
          icon={GraduationCap}
          accent="#234333"
        />
        <StatCard
          testid="stat-executive"
          label="Executive Body"
          value={loading ? "—" : summary?.executive_count ?? 0}
          hint="Trustees on record"
          icon={UserSquare}
          accent="#234333"
        />
        <StatCard
          testid="stat-general"
          label="General Body"
          value={loading ? "—" : summary?.general_count ?? 0}
          hint="Members on register"
          icon={Users}
          accent="#234333"
        />
        <StatCard
          testid="stat-banks"
          label="Bank Accounts"
          value={loading ? "—" : summary?.bank_count ?? 0}
          hint="Active treasury"
          icon={Landmark}
          accent="#d4af37"
        />
        <StatCard
          testid="stat-documents"
          label="Documents"
          value={loading ? "—" : summary?.document_count ?? 0}
          hint="Trust records on file"
          icon={FileText}
          accent="#234333"
        />
        <StatCard
          testid="stat-immovables"
          label="Lands & Buildings"
          value={loading ? "—" : summary?.immovable_count ?? 0}
          hint={
            loading
              ? ""
              : `${summary?.land_count ?? 0} lands · ${summary?.property_count ?? 0} buildings`
          }
          icon={MapPin}
          accent="#d4af37"
        />
        <StatCard
          testid="stat-current-fy"
          label={`FY ${summary?.current_fy_label || "—"}`}
          value={loading ? "—" : `${summary?.current_fy_compliance ?? 0}%`}
          hint="Compliance this FY"
          icon={BookOpen}
          accent="#234333"
        />
        <StatCard
          testid="stat-gallery"
          label="Gallery Photos"
          value={loading ? "—" : summary?.gallery_count ?? 0}
          hint="Moments captured"
          icon={Images}
          accent="#d4af37"
        />
        <StatCard
          testid="stat-designations"
          label="Designations"
          value={loading ? "—" : summary?.designation_count ?? 0}
          hint="Master list entries"
          icon={ListChecks}
          accent="#d4af37"
        />
        <StatCard
          testid="stat-institute-types"
          label="Institute Types"
          value={loading ? "—" : summary?.institute_type_count ?? 0}
          hint="Classification master"
          icon={Building2}
          accent="#234333"
        />
        <StatCard
          testid="stat-bank-purposes"
          label="Bank Purposes"
          value={loading ? "—" : summary?.bank_purpose_count ?? 0}
          hint="Account purpose master"
          icon={Landmark}
          accent="#d4af37"
        />
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Link
          to="/admin/trust"
          data-testid="quick-link-trust"
          className="group bg-white border border-[#e5e0d8] rounded-sm p-6 hover:border-[#234333]/60 transition-colors"
        >
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b]">Step 01</div>
          <div className="mt-2 font-serif text-2xl text-[#1c1a17]">Trust Master Details</div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Capture legal, registration and office information — one-time master record.
          </div>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#234333] group-hover:gap-3 transition-all">
            Fill details <ArrowRight size={14} strokeWidth={1.5} />
          </div>
        </Link>

        <Link
          to="/admin/institutes"
          data-testid="quick-link-institutes"
          className="group bg-white border border-[#e5e0d8] rounded-sm p-6 hover:border-[#234333]/60 transition-colors"
        >
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b]">Step 02</div>
          <div className="mt-2 font-serif text-2xl text-[#1c1a17]">Institute Mapping</div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Register each institute — address, type, approvals, year of establishment.
          </div>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#234333] group-hover:gap-3 transition-all">
            Manage institutes <ArrowRight size={14} strokeWidth={1.5} />
          </div>
        </Link>

        <Link
          to="/admin/masters"
          data-testid="quick-link-masters"
          className="group bg-white border border-[#e5e0d8] rounded-sm p-6 hover:border-[#234333]/60 transition-colors"
        >
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b]">Step 03</div>
          <div className="mt-2 font-serif text-2xl text-[#1c1a17]">Masters</div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Maintain dropdowns for Designation, Institute Type, and Bank Purpose.
          </div>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#234333] group-hover:gap-3 transition-all">
            Edit masters <ArrowRight size={14} strokeWidth={1.5} />
          </div>
        </Link>
      </div>

      {summary?.updated_at && (
        <div className="mt-10 text-xs text-[#6b655b]" data-testid="trust-last-updated">
          Trust record last updated {new Date(summary.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
